import { 
  InvestmentOpportunity, 
  InvestmentOpportunityFormData, 
  InvestmentOpportunityFilters,
  InvestmentStatus,
  RiskLevel 
} from '../types/InvestmentOpportunity';

// Mock data for development
const MOCK_INVESTMENT_OPPORTUNITIES: InvestmentOpportunity[] = [
  {
    id: '1',
    title: 'Industri Gitar Baki',
    description: 'Pusat manufaktur gitar terkenal dunia dengan potensi ekspor yang besar',
    category: 'Industri Manufaktur',
    status: InvestmentStatus.AVAILABLE,
    location: 'Sukoharjo',
    rating: 4.5,
    image: '/api/placeholder/400/300',
    investmentAmount: 500000000,
    expectedReturn: 15,
    duration: 24,
    riskLevel: RiskLevel.MEDIUM,
    sectors: ['Gitar Baki', 'Kerajinan'],
    contactPerson: 'Budi Santoso',
    contactEmail: 'budi@guitarbaki.com',
    contactPhone: '08123456789',
    establishedDate: new Date('2020-01-15'),
    lastUpdated: new Date(),
    isActive: true
  },
  {
    id: '2',
    title: 'Industri Tekstil',
    description: 'Sektor manufaktur tekstil dan garmen yang berkembang pesat',
    category: 'Industri Manufaktur',
    status: InvestmentStatus.ACTIVE,
    location: 'Sukoharjo',
    rating: 4.2,
    image: '/api/placeholder/400/300',
    investmentAmount: 750000000,
    expectedReturn: 18,
    duration: 36,
    riskLevel: RiskLevel.LOW,
    sectors: ['Tekstil'],
    contactPerson: 'Sari Dewi',
    contactEmail: 'sari@tekstil.com',
    contactPhone: '08123456790',
    establishedDate: new Date('2019-03-20'),
    lastUpdated: new Date(),
    isActive: true
  },
  {
    id: '3',
    title: 'Agroindustri',
    description: 'Pengolahan hasil pertanian dan perkebunan',
    category: 'Pertanian',
    status: InvestmentStatus.AVAILABLE,
    location: 'Sukoharjo',
    rating: 4.0,
    image: '/api/placeholder/400/300',
    investmentAmount: 300000000,
    expectedReturn: 12,
    duration: 18,
    riskLevel: RiskLevel.MEDIUM,
    sectors: ['Agroindustri'],
    contactPerson: 'Ahmad Rizki',
    contactEmail: 'ahmad@agro.com',
    contactPhone: '08123456791',
    establishedDate: new Date('2021-06-10'),
    lastUpdated: new Date(),
    isActive: true
  },
  {
    id: '4',
    title: 'Industri Furniture',
    description: 'Manufaktur mebel dan furniture berkualitas ekspor',
    category: 'Industri Manufaktur',
    status: InvestmentStatus.COMPLETED,
    location: 'Sukoharjo',
    rating: 4.8,
    image: '/api/placeholder/400/300',
    investmentAmount: 1000000000,
    expectedReturn: 20,
    duration: 48,
    riskLevel: RiskLevel.LOW,
    sectors: ['Furniture'],
    contactPerson: 'Indra Kusuma',
    contactEmail: 'indra@furniture.com',
    contactPhone: '08123456792',
    establishedDate: new Date('2018-09-12'),
    lastUpdated: new Date(),
    isActive: true
  },
  {
    id: '5',
    title: 'Industri Otomotif',
    description: 'Komponen dan suku cadang otomotif',
    category: 'Industri Manufaktur',
    status: InvestmentStatus.SUSPENDED,
    location: 'Sukoharjo',
    rating: 3.8,
    image: '/api/placeholder/400/300',
    investmentAmount: 2000000000,
    expectedReturn: 22,
    duration: 60,
    riskLevel: RiskLevel.HIGH,
    sectors: ['Otomotif'],
    contactPerson: 'Rudi Hartono',
    contactEmail: 'rudi@otomotif.com',
    contactPhone: '08123456793',
    establishedDate: new Date('2017-11-05'),
    lastUpdated: new Date(),
    isActive: false
  },
  {
    id: '6',
    title: 'Pariwisata',
    description: 'Pengembangan destinasi wisata dan hospitality',
    category: 'Pariwisata',
    status: InvestmentStatus.AVAILABLE,
    location: 'Sukoharjo',
    rating: 4.3,
    image: '/api/placeholder/400/300',
    investmentAmount: 800000000,
    expectedReturn: 16,
    duration: 30,
    riskLevel: RiskLevel.MEDIUM,
    sectors: ['Kuliner'],
    contactPerson: 'Maya Sari',
    contactEmail: 'maya@pariwisata.com',
    contactPhone: '08123456794',
    establishedDate: new Date('2022-02-14'),
    lastUpdated: new Date(),
    isActive: true
  }
];

class InvestmentOpportunityService {
  private mockData: InvestmentOpportunity[] = [...MOCK_INVESTMENT_OPPORTUNITIES];

  // Get all investment opportunities with optional filtering
  async getAll(filters?: InvestmentOpportunityFilters): Promise<InvestmentOpportunity[]> {
    return new Promise((resolve) => {
      setTimeout(() => {
        let filtered = [...this.mockData];

        if (filters) {
          if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(item => 
              item.title.toLowerCase().includes(searchTerm) ||
              item.description.toLowerCase().includes(searchTerm) ||
              item.location.toLowerCase().includes(searchTerm)
            );
          }

          if (filters.category) {
            filtered = filtered.filter(item => item.category === filters.category);
          }

          if (filters.status !== undefined) {
            filtered = filtered.filter(item => item.status === filters.status);
          }

          if (filters.riskLevel) {
            filtered = filtered.filter(item => item.riskLevel === filters.riskLevel);
          }

          if (filters.minInvestment !== undefined) {
            filtered = filtered.filter(item => item.investmentAmount >= filters.minInvestment!);
          }

          if (filters.maxInvestment !== undefined) {
            filtered = filtered.filter(item => item.investmentAmount <= filters.maxInvestment!);
          }

          if (filters.location) {
            filtered = filtered.filter(item => 
              item.location.toLowerCase().includes(filters.location!.toLowerCase())
            );
          }
        }

        resolve(filtered.sort((a, b) => 
          new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
        ));
      }, 300);
    });
  }

  // Get single investment opportunity by ID
  async getById(id: string): Promise<InvestmentOpportunity | null> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const opportunity = this.mockData.find(item => item.id === id);
        resolve(opportunity || null);
      }, 200);
    });
  }

  // Create new investment opportunity
  async create(data: InvestmentOpportunityFormData): Promise<InvestmentOpportunity> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const newOpportunity: InvestmentOpportunity = {
          id: (this.mockData.length + 1).toString(),
          ...data,
          image: typeof data.image === 'string' ? data.image : '/api/placeholder/400/300',
          establishedDate: new Date(),
          lastUpdated: new Date()
        };

        this.mockData.push(newOpportunity);
        resolve(newOpportunity);
      }, 500);
    });
  }

  // Update existing investment opportunity
  async update(id: string, data: Partial<InvestmentOpportunityFormData>): Promise<InvestmentOpportunity> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const index = this.mockData.findIndex(item => item.id === id);
        
        if (index === -1) {
          reject(new Error('Investment opportunity not found'));
          return;
        }

        const updatedOpportunity = {
          ...this.mockData[index],
          ...data,
          image: typeof data.image === 'string' ? data.image : this.mockData[index].image,
          lastUpdated: new Date()
        };

        this.mockData[index] = updatedOpportunity;
        resolve(updatedOpportunity);
      }, 500);
    });
  }

  // Delete investment opportunity
  async delete(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const index = this.mockData.findIndex(item => item.id === id);
        
        if (index === -1) {
          reject(new Error('Investment opportunity not found'));
          return;
        }

        this.mockData.splice(index, 1);
        resolve();
      }, 300);
    });
  }

  // Toggle active status
  async toggleActive(id: string): Promise<InvestmentOpportunity> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const index = this.mockData.findIndex(item => item.id === id);
        
        if (index === -1) {
          reject(new Error('Investment opportunity not found'));
          return;
        }

        this.mockData[index] = {
          ...this.mockData[index],
          isActive: !this.mockData[index].isActive,
          lastUpdated: new Date()
        };

        resolve(this.mockData[index]);
      }, 300);
    });
  }

  // Get statistics
  async getStatistics(): Promise<{
    total: number;
    available: number;
    active: number;
    completed: number;
    suspended: number;
    totalInvestmentAmount: number;
  }> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const stats = this.mockData.reduce(
          (acc, item) => {
            acc.total++;
            acc.totalInvestmentAmount += item.investmentAmount;
            
            switch (item.status) {
              case InvestmentStatus.AVAILABLE:
                acc.available++;
                break;
              case InvestmentStatus.ACTIVE:
                acc.active++;
                break;
              case InvestmentStatus.COMPLETED:
                acc.completed++;
                break;
              case InvestmentStatus.SUSPENDED:
                acc.suspended++;
                break;
            }
            
            return acc;
          },
          { total: 0, available: 0, active: 0, completed: 0, suspended: 0, totalInvestmentAmount: 0 }
        );

        resolve(stats);
      }, 200);
    });
  }
}

export const investmentOpportunityService = new InvestmentOpportunityService();